import L from "leaflet";

/** Shared marker color tokens for live equipment status. */
export const EQUIPMENT_STATUS_COLORS: Record<string, string> = {
	online: "#22c55e",
	offline: "#ef4444",
	error: "#f97316",
};

export const EQUIPMENT_STATUS_LABELS: Record<string, string> = {
	online: "Online",
	offline: "Offline",
	error: "Xato",
};

/** Color for the yellow "online but outside its brigade's territory" state. */
export const EQUIPMENT_OUTSIDE_TERRITORY_COLOR = "#eab308";

/**
 * Equipment marker color: offline/error take priority (device-level problem),
 * otherwise online equipment is green unless it has drifted outside its
 * brigade's assigned territory, which reads as yellow.
 */
export function equipmentStatusColor(status: string, insideTerritory?: boolean | null): string {
	if (status === "online" && insideTerritory === false) {
		return EQUIPMENT_OUTSIDE_TERRITORY_COLOR;
	}
	return EQUIPMENT_STATUS_COLORS[status] ?? "#6b7280";
}

/** 4-state live movement status colors (TZ §9.5: moving/stopped/no signal/never connected). */
export const MOVEMENT_STATE_COLORS: Record<string, string> = {
	MOVING: "#22c55e",
	STOPPED: "#eab308",
	NO_SIGNAL: "#ef4444",
	NEVER_CONNECTED: "#6b7280",
};

export const MOVEMENT_STATE_LABELS: Record<string, string> = {
	MOVING: "Harakatda",
	STOPPED: "To'xtagan",
	NO_SIGNAL: "Signal yo'q",
	NEVER_CONNECTED: "Ulanmagan",
};

/**
 * Top-down car silhouette (nose pointing up, i.e. heading 0deg = north) so it
 * reads correctly on a map, which is a bird's-eye view rather than a 3/4-front
 * elevation. Rounded body + a brighter front windshield / dimmer rear window
 * + side mirrors near the windshield to make the facing direction unambiguous.
 */
function carSvgMarkup(color: string): string {
	return `
		<rect x="7" y="3" width="10" height="18" rx="3" fill="${color}" stroke="white" stroke-width="1"/>
		<rect x="5.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/>
		<rect x="17.2" y="7.5" width="1.6" height="1.1" rx="0.4" fill="${color}" stroke="white" stroke-width="0.6"/>
		<rect x="8.5" y="6" width="7" height="4" rx="1.3" fill="white" fill-opacity="0.85"/>
		<rect x="8.5" y="15" width="7" height="2.8" rx="1" fill="white" fill-opacity="0.55"/>`;
}

/** Static car icon (status color + size). Used where heading is irrelevant. */
export function createCarIcon(status: string, size = 28) {
	const color = EQUIPMENT_STATUS_COLORS[status] ?? "#6b7280";
	return L.divIcon({
		className: "",
		html: `<div style="display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">${carSvgMarkup(color)}</svg></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size / 2],
		popupAnchor: [0, -(size / 2 + 2)],
	});
}

/** Rotated car icon builder shared by status- and movement-state-based factories. */
function createRotatedCarIcon(color: string, heading: number | null) {
	const rotation = heading ?? 0;
	return L.divIcon({
		className: "",
		html: `
			<div style="transform:rotate(${rotation}deg);display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))">
				<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24">${carSvgMarkup(color)}</svg>
			</div>`,
		iconSize: [34, 34],
		iconAnchor: [17, 17],
		popupAnchor: [0, -18],
	});
}

/** Rotated car icon (GPS device status color + heading, with an outside-territory yellow state). */
export function createLiveCarIcon(
	status: string,
	heading: number | null,
	insideTerritory?: boolean | null
) {
	return createRotatedCarIcon(equipmentStatusColor(status, insideTerritory), heading);
}

/** Rotated car icon colored by the derived 4-state movement status (TZ §9.5). */
export function createMovementCarIcon(movementState: string, heading: number | null) {
	return createRotatedCarIcon(MOVEMENT_STATE_COLORS[movementState] ?? "#6b7280", heading);
}

/** Person/staff marker icon (used for live brigadier-staff location). */
export function createStaffIcon(isOnline: boolean, size = 28) {
	const color = isOnline ? "#1677ff" : "#94a3b8";
	return L.divIcon({
		className: "",
		html: `<div style="display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size],
		popupAnchor: [0, -size],
	});
}

/** Plant / planting marker icon (color is resolved by the caller). */
export function createPlantIcon(color: string, size = 26) {
	return L.divIcon({
		className: "",
		html: `<div style="display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1"><path d="M12 2C8 2 5 5 5 9c0 3 2 5.5 5 6.3V20H8v2h8v-2h-2v-4.7c3-.8 5-3.3 5-6.3 0-4-3-7-7-7z"/></svg></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size],
		popupAnchor: [0, -size],
	});
}

/** Attendance event marker icon (check-in geo points). */
export function createAttendanceIcon(insideTerritory: boolean | null, size = 28) {
	// Green inside territory, amber if outside/unknown.
	const color = insideTerritory === false ? "#f59e0b" : "#16a34a";
	return L.divIcon({
		className: "",
		html: `<div style="display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))"><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg></div>`,
		iconSize: [size, size],
		iconAnchor: [size / 2, size],
		popupAnchor: [0, -size],
	});
}
